import React, { useEffect, useState, useRef, useMemo } from "react";
import { Bell, FileText, Sun, Moon, ChevronDown, Menu } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useHeaderStore } from "../store/headerStore";
import { useSidebarStore } from "../store/sidebarStore";
import { useAuth } from "../auth/AuthContext";
import ProfileDropdown from "./ProfileDropdown";
import FeedbackNotificationBell from "./FeedbackNotificationBell";
import "../styles/global-header.css";

function avatarInitial(email?: string, name?: string): string {
  if (name && name.trim()) return name.trim().charAt(0).toUpperCase();
  if (email && email.trim()) return email.trim().charAt(0).toUpperCase();
  return "?";
}

const AVATAR_BG: Record<string, string> = {
  admin: "rgba(239,68,68,0.2)",
  analyst: "rgba(168,85,247,0.2)",
  client: "rgba(0,102,224,0.2)",
};
const AVATAR_COLOR: Record<string, string> = {
  admin: "#ef4444",
  analyst: "#a855f7",
  client: "#818cf8",
};

const ROUTE_META: Record<string, string[]> = {
  "/dashboard": ["Dashboard", "Overview"],
  "/leaks": ["Intelligence Feed", "Posts"],
  "/leaks/saved": ["Intelligence Feed", "Saved posts"],
  "/leaks/exports": ["Intelligence Feed", "Export history"],
  "/timeline": ["Timeline", "View"],
  "/customer-check": ["Customer Check", "Search"],
  "/domain-monitor": ["Domain Monitor", "Overview"],
  "/alerts": ["Alert Config", "Settings"],
  "/takedowns": ["Takedowns", "Management"],
  "/contribute": ["Contribute", "Submit"],
  "/subscription": ["Settings", "Subscription"],
  "/api-access": ["Developers", "API Keys & Metering"],
  "/profile": ["Settings", "Profile"],
  "/profile/security": ["Settings", "Security"],
  "/feedback": ["App", "Feedback"],
  "/coupon": ["App", "Coupon"],
  "/help-center": ["Help Center", "Overview"],
  "/help-center/wiki": ["Help Center", "Wiki"],
  "/help-center/contact": ["Help Center", "Contact"],
  "/admin": ["Admin", "Dashboard"],
  "/access": ["Admin", "Access"],
  "/engagement-board": ["Admin", "Engagement"],
  "/admin/audit": ["Admin", "Audit Logs"],
  "/admin/forum-monitoring": ["Admin", "Forum Monitoring"],
  "/admin/forum_monitoring": ["Admin", "Forum Monitoring"],
};

function getDeterministicColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = hash % 360;
  return `hsl(${h}, 70%, 50%)`;
}

interface GlobalHeaderProps {
  theme: "light" | "dark";
  onToggleTheme: () => void;
}

export default function GlobalHeader({ theme, onToggleTheme }: GlobalHeaderProps) {
  const { user } = useAuth();
  const { pageTitleOverride, setPageTitleOverride } = useHeaderStore();
  const { toggleMobileOpen } = useSidebarStore();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [healthStatus, setHealthStatus] = useState<"ok" | "degraded" | "error">("error");
  const [healthTime, setHealthTime] = useState<Date | null>(null);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [showReportsNew, setShowReportsNew] = useState(() => !localStorage.getItem("reports_clicked"));
  const workspaceRef = useRef<HTMLDivElement>(null);

  // Scroll listener for shadow
  useEffect(() => {
    const handleScroll = () => {
      // Find the main scrollable container. It is the sibling of the header in MainLayout.
      const scrollContainer = document.querySelector(".main-area > div.overflow-y-auto");
      if (scrollContainer) {
        setIsScrolled(scrollContainer.scrollTop > 4);
      }
    };
    const scrollContainer = document.querySelector(".main-area > div.overflow-y-auto");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Health check polling
  useEffect(() => {
    const checkHealth = async () => {
      if (document.hidden) return;
      try {
        const start = performance.now();
        const res = await fetch("/api/health");
        const elapsed = performance.now() - start;
        if (res.ok) {
          setHealthStatus(elapsed > 2000 ? "degraded" : "ok");
          setHealthTime(new Date());
        } else {
          setHealthStatus("error");
        }
      } catch (e) {
        setHealthStatus("error");
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 60000);
    const visibilityHandler = () => { if (!document.hidden) checkHealth(); };
    document.addEventListener("visibilitychange", visibilityHandler);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, []);

  // Workspace outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (workspaceRef.current && !workspaceRef.current.contains(e.target as Node)) {
        setShowWorkspace(false);
      }
    };
    if (showWorkspace) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showWorkspace]);
  
  // Escape key for popovers
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowWorkspace(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, []);

  const role = user?.role ?? "client";
  const initial = avatarInitial(user?.email, user?.name);
  
  // Workspace info
  const workspaceName = user?.organization || "My Workspace";
  const wsInitial = workspaceName.charAt(0).toUpperCase();
  const wsBg = getDeterministicColor(workspaceName);

  // Profile completion
  const { completionPct } = useMemo(() => {
    if (!user) return { completionPct: 0 };
    const fields = ["first_name", "last_name", "organization", "job_title"];
    let filled = 0;
    fields.forEach(f => {
      // @ts-ignore
      if (user[f] && user[f].trim()) filled++;
    });
    return { completionPct: Math.round((filled / 4) * 100) };
  }, [user]);

  const circleLength = 2 * Math.PI * 20; // r=20 for 44px svg box
  const strokeDashoffset = circleLength - (completionPct / 100) * circleLength;

  // Breadcrumb logic
  const currentPath = location.pathname;
  let breadcrumbSegments = ROUTE_META[currentPath];
  if (!breadcrumbSegments) {
    const parts = currentPath.split("/").filter(Boolean);
    if (pageTitleOverride) {
      let parent = "App";
      if (parts[0] === "leaks") parent = "Intelligence Feed";
      else if (parts[0] === "admin") parent = "Admin";
      breadcrumbSegments = [parent, pageTitleOverride];
    } else {
      const last = parts[parts.length - 1] || "";
      const titleCased = last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, " ");
      breadcrumbSegments = ["App", titleCased];
    }
  }

  const handleReportsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowReportsNew(false);
    localStorage.setItem("reports_clicked", "true");
  };

  return (
    <header className={`global-header ${isScrolled ? "is-scrolled" : ""}`} aria-label="Top navigation">
      
      {/* LEFT GROUP */}
      <div className="global-header-left">
        {/* Mobile menu toggle */}
        <button
          className="mobile-sidebar-toggle"
          onClick={toggleMobileOpen}
          aria-label="Toggle navigation menu"
          title="Navigation menu"
        >
          <Menu size={18} />
        </button>

        {/* 1. Status Ring */}
        <div className="header-tooltip-wrap">
          <div className="status-ring-wrap" tabIndex={0} aria-label="API Status">
            <div className={`status-ring status-${healthStatus}`} />
          </div>
          <div className="header-tooltip">
            {healthStatus === "ok" ? "Live feed connected" : healthStatus === "degraded" ? "Live feed slow" : "Live feed disconnected"}
            {healthTime && healthStatus !== "error" && ` (updated ${healthTime.toLocaleTimeString()})`}
          </div>
        </div>

        {/* 2. Workspace Chip */}
        <div className="workspace-wrap" ref={workspaceRef} style={{ position: "relative" }}>
          <button 
            className="workspace-chip" 
            onClick={() => setShowWorkspace(!showWorkspace)}
            aria-expanded={showWorkspace}
            aria-haspopup="true"
          >
            <div className="workspace-avatar" style={{ background: wsBg }}>{wsInitial}</div>
            <span className="workspace-name">{workspaceName}</span>
            <ChevronDown size={14} className="workspace-chevron" />
          </button>
          
          {showWorkspace && (
            <div className="header-popover workspace-popover" role="menu">
              <div className="popover-row" style={{ fontWeight: 600 }}>
                {workspaceName}
              </div>
              <div className="popover-row" style={{ borderBottom: "1px solid var(--color-border)", paddingBottom: "12px", marginBottom: "4px" }}>
                <span className={`role-badge role-${role}`} style={{ fontSize: "10px" }}>{role.toUpperCase()}</span>
              </div>
              <div className="popover-row disabled" role="menuitem" aria-disabled="true">
                <span>Switch workspace</span>
                <span className="coming-soon-pill">COMING SOON</span>
              </div>
            </div>
          )}
        </div>

        <span className="header-separator">/</span>

        {/* 3. Breadcrumb */}
        <nav className="breadcrumb-nav" aria-label="Breadcrumb">
          {breadcrumbSegments.map((segment, idx) => {
            const isLast = idx === breadcrumbSegments.length - 1;
            return (
              <React.Fragment key={idx}>
                {idx > 0 && <span className="header-separator" style={{ margin: "0 4px" }}>/</span>}
                {isLast ? (
                  <span className="breadcrumb-item current" aria-current="page">{segment}</span>
                ) : (
                  <span className="breadcrumb-item parent-segment">{segment}</span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {/* RIGHT GROUP */}
      <div className="global-header-right">
        
        {/* 4. SET ALERTS */}
        {(role === "client" || role === "admin") && (
          <NavLink 
            to="/alerts" 
            className={({ isActive }) => `header-action-link ${isActive ? "active" : ""}`}
            title="Set Alerts"
          >
            <Bell size={18} className="bell-icon" />
            <span className="header-action-text">SET ALERTS</span>
          </NavLink>
        )}

        {/* 5. EXCLUSIVE REPORTS */}
        <NavLink 
          to="/reports" 
          className={({ isActive }) => `header-action-link ${isActive ? "active" : ""}`}
          onClick={() => {
            setShowReportsNew(false);
            localStorage.setItem("reports_clicked", "true");
          }}
        >
          <FileText size={18} />
          <span className="header-action-text">EXCLUSIVE REPORTS</span>
          {showReportsNew && <span className="new-pill">NEW</span>}
        </NavLink>

        {/* 6. Theme Toggle */}
        <div className="header-tooltip-wrap">
          <button
            onClick={onToggleTheme}
            className="theme-toggle-btn"
            aria-pressed={theme === "dark"}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          >
            {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
          </button>
          <div className="header-tooltip">Switch to {theme === "dark" ? "light" : "dark"} mode</div>
        </div>

        {/* 7. Admin Feedback Notifications */}
        {user && (role === "admin" || role === "super_admin" || role === "analyst") && (
          <FeedbackNotificationBell />
        )}

        {/* 8. Profile Avatar Dropdown */}
        {user && <ProfileDropdown completionPct={completionPct} />}
      </div>
    </header>
  );
}
